import { useState } from 'react';
import { toast } from 'sonner';

import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';

export interface ReportPreferences {
  enabled: boolean;
  cadence: 'weekly' | 'monthly' | 'quarterly';
  recipients: string[];
}

interface UseReportPreferencesReturn {
  preferences: ReportPreferences;
  isLoading: boolean;
  savePreferences: (prefs: ReportPreferences) => Promise<void>;
  isSaving: boolean;
}

/**
 * Validates that a recipient email belongs to the same domain as the user's email.
 *
 * @param recipientEmail - Email address to validate
 * @param userEmail - The authenticated user's email for domain comparison
 * @returns true if domains match, false otherwise
 */
export function validateRecipientDomain(
  recipientEmail: string,
  userEmail: string
): boolean {
  if (!recipientEmail || !userEmail) return false;
  const recipientParts = recipientEmail.split('@');
  const userParts = userEmail.split('@');
  if (recipientParts.length !== 2 || userParts.length !== 2) return false;
  if (
    !recipientParts[0] ||
    !recipientParts[1] ||
    !userParts[0] ||
    !userParts[1]
  )
    return false;
  return recipientParts[1].toLowerCase() === userParts[1].toLowerCase();
}

/**
 * Hook to read and write report scheduling preferences.
 *
 * Reads: report-enabled, report-cadence, report-recipients from useMy({ resource: 'setting' })
 * Writes: useUpdateSetting() with silent: true (shows own toast on success)
 *
 * @param email - Optional customer email for impersonation support
 */
export function useReportPreferences(
  email?: string
): UseReportPreferencesReturn {
  const { data: settings, isPending: isLoading } = useMy(
    { resource: 'setting' },
    {
      email,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: updateSetting } = useUpdateSetting(email);

  const preferences: ReportPreferences = {
    enabled: settings?.find(s => s.name === 'report-enabled')?.value === 'true',
    cadence:
      (settings?.find(s => s.name === 'report-cadence')
        ?.value as ReportPreferences['cadence']) || 'weekly',
    recipients: (() => {
      const val = settings?.find(s => s.name === 'report-recipients')?.value;
      if (!val || typeof val !== 'string') return [];
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    })(),
  };

  async function savePreferences(prefs: ReportPreferences): Promise<void> {
    setIsSaving(true);
    try {
      await updateSetting({
        name: 'report-enabled',
        value: String(prefs.enabled),
        silent: true,
      });
      await updateSetting({
        name: 'report-cadence',
        value: prefs.cadence,
        silent: true,
      });
      await updateSetting({
        name: 'report-recipients',
        value: JSON.stringify(prefs.recipients),
        silent: true,
      });
      toast.success('Report preferences saved');
    } catch (error) {
      toast.error('Failed to save report preferences');
      throw error;
    } finally {
      setIsSaving(false);
    }
  }

  return {
    preferences,
    isLoading,
    savePreferences,
    isSaving,
  };
}
